import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, PartyPopper } from "lucide-react";
import { CREW } from "../content";
import { Reveal, Eyebrow } from "../components/site/Reveal";
import { CrewPhoto } from "../components/site/CrewPhoto";
import { LogoMark } from "../components/site/Logo";

const CrewRow = ({ c, i }) => {
  const flip = i % 2 === 1;
  return (
    <Reveal>
      <motion.div
        whileHover={{ y: -4 }}
        data-testid={`crew-detail-${c.name.split(" ")[0].toLowerCase()}`}
        className={`sticker rounded-[32px] bg-white p-6 sm:p-10 grid sm:grid-cols-12 gap-8 items-center ${flip ? "sm:[direction:rtl]" : ""}`}
      >
        {c.age !== "parent" && (
          <div className="sm:col-span-4 [direction:ltr]">
            <CrewPhoto c={c} className="h-64 sm:h-72" />
          </div>
        )}
        <div className={`[direction:ltr] ${c.age !== "parent" ? "sm:col-span-8" : "sm:col-span-12"}`}>
          <p className="font-hand text-2xl" style={{ color: c.color === "var(--sun)" ? "var(--coral)" : c.color }}>
            {c.role}
          </p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl mt-1">
            {c.name} <span className="text-lg font-medium text-[var(--ink-soft)]">· {c.age}</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[var(--ink-soft)]">{c.bio}</p>
        </div>
      </motion.div>
    </Reveal>
  );
};

export default function CrewPage() {
  const kids = CREW.filter((c) => c.age !== "parent");
  const parents = CREW.filter((c) => c.age === "parent");
  return (
    <main data-testid="crew-page" className="pb-24">
      <header className="sticky top-0 z-50 bg-[var(--cream)]/85 backdrop-blur-md border-b-[3px] border-[var(--ink)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="crew-home-link" className="flex items-center gap-2">
            <ArrowLeft size={18} />
            <span className="flex items-center gap-2.5">
              <LogoMark />
              <span className="font-display font-semibold">The Yard Party Project</span>
            </span>
          </Link>
          <Link
            to="/#book"
            data-testid="crew-book-btn"
            className="sticker-sm rounded-full bg-[var(--coral)] text-white font-display font-semibold px-5 py-2.5 text-sm"
          >
            Book a sign
          </Link>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className="pt-16 pb-10 text-center">
          <Reveal>
            <div className="flex justify-center">
              <Eyebrow color="var(--sun)"><PartyPopper size={14} /> Meet the crew</Eyebrow>
            </div>
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[0.95]">
              The kids behind <br />the <span className="text-[var(--coral)]">smiles.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[var(--ink-soft)] max-w-xl mx-auto">
              Four kids, two parent helpers, one van full of yard signs. Here's who does what.
            </p>
          </Reveal>
        </section>
        <div className="space-y-10">
          {kids.map((c, i) => <CrewRow key={c.name} c={c} i={i} />)}
        </div>
        <Reveal className="mt-20">
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-center">And the parent helpers</h2>
        </Reveal>
        <div className="mt-10 space-y-10">
          {parents.map((c, i) => <CrewRow key={c.name} c={c} i={i + kids.length} />)}
        </div>
      </div>
    </main>
  );
}
